import { ApolloLink } from '@apollo/client/link';
import { Observable } from '@apollo/client/utilities';

class DebounceLink extends ApolloLink {
    constructor(defaultDelay) {
        super();
        this.debounceInfo = {};
        this.defaultDelay = defaultDelay;

        this.cleanup = (debounceKey, groupId) => {
            const dbi = this.debounceInfo[debounceKey];
            if (!dbi) {
                return;
            }
            delete dbi.runningSubscriptions[groupId];
            if (groupId === dbi.currentGroupId) {
                clearTimeout(dbi.timeout);
            }
            const noRunningSubscriptions = Object.keys(dbi.runningSubscriptions).length === 0;
            const noQueuedObservers = dbi.queuedObservers.length === 0;
            if (noRunningSubscriptions && noQueuedObservers) {
                delete this.debounceInfo[debounceKey];
            }
        };

        this.unsubscribe = (debounceKey, debounceGroupId, observer) => {
            const isNotObserver = obs => obs !== observer;
            const dbi = this.debounceInfo[debounceKey];
            if (!dbi) {
                return;
            }
            if (debounceGroupId === dbi.currentGroupId) {
                dbi.queuedObservers = dbi.queuedObservers.filter(isNotObserver);
                if (dbi.queuedObservers.length === 0) {
                    this.cleanup(debounceKey, debounceGroupId);
                }
                return;
            }
            const observerGroup = dbi.runningSubscriptions[debounceGroupId];
            if (observerGroup) {
                observerGroup.observers = observerGroup.observers.filter(isNotObserver);
                if (observerGroup.observers.length === 0) {
                    observerGroup.subscription.unsubscribe();
                    this.cleanup(debounceKey, debounceGroupId);
                }
            }
        };
    }

    request(operation, forward) {
        const { debounceKey, debounceTimeout } = operation.getContext();
        if (!debounceKey) {
            return forward(operation);
        }
        return new Observable(observer => {
            const debounceGroupId = this.enqueueRequest(
                { debounceKey, debounceTimeout },
                { operation, forward, observer }
            );
            return () => {
                this.unsubscribe(debounceKey, debounceGroupId, observer);
            };
        });
    }

    setupDebounceInfo(debounceKey) {
        this.debounceInfo[debounceKey] = {
            runningSubscriptions: {},
            queuedObservers: [],
            currentGroupId: 0,
            timeout: undefined,
            lastRequest: undefined
        };
        return this.debounceInfo[debounceKey];
    }

    enqueueRequest({ debounceKey, debounceTimeout }, { operation, forward, observer }) {
        const dbi = this.debounceInfo[debounceKey] || this.setupDebounceInfo(debounceKey);
        dbi.queuedObservers.push(observer);
        dbi.lastRequest = { operation, forward };
        if (dbi.timeout) {
            clearTimeout(dbi.timeout);
        }
        dbi.timeout = setTimeout(() => this.flush(debounceKey), debounceTimeout || this.defaultDelay);
        return dbi.currentGroupId;
    }

    flush(debounceKey) {
        const dbi = this.debounceInfo[debounceKey];
        if (dbi.queuedObservers.length === 0 || typeof dbi.lastRequest === 'undefined') {
            return;
        }
        const { operation, forward } = dbi.lastRequest;
        const currentObservers = dbi.queuedObservers.slice();
        const groupId = dbi.currentGroupId;
        const sub = forward(operation).subscribe({
            next: v => {
                currentObservers.forEach(observer => observer.next && observer.next(v));
            },
            error: e => {
                currentObservers.forEach(observer => observer.error && observer.error(e));
                this.cleanup(debounceKey, groupId);
            },
            complete: () => {
                currentObservers.forEach(observer => observer.complete && observer.complete());
                this.cleanup(debounceKey, groupId);
            }
        });
        dbi.runningSubscriptions[dbi.currentGroupId] = {
            subscription: sub,
            observers: currentObservers
        };
        dbi.queuedObservers = [];
        dbi.currentGroupId++;
    }
}

export default DebounceLink;
